/**
 * Per-run state.
 *
 * The line this file draws: **run state resets, progression persists.** Nothing
 * here is meta-progression, and nothing in progression.ts is run-scoped. The one
 * seam between them is `bankRun()`, which reads a finished run and feeds the
 * five tracks. Keeping that seam to a single function is what stops the two
 * lifetimes from leaking into each other.
 *
 * Terrain lives here as a diff log, not as cells (docs/01 §8) — the run carries
 * its seed plus what the player did to the world, and the world is re-derived.
 *
 * Decoupled by design: weapons, modules, biomes, rooms and enemies are plain
 * string ids. No imports from combat/, world/, entity/ or ui/.
 */

import { DEFAULT_ARCHETYPE, getArchetype } from './archetypes';
import type { Revival, TraversalAbilityId } from './archetypes';
import { getKit } from './kits';
import { Factions, createFactionState } from './factions';
import type { DeedId, FactionId, FactionState, RepChange } from './factions';
import { echoForSeed, makeMapEcho } from './mapEcho';
import { NO_PASSIVES, resolvePassives } from './passives';
import type { RunPassives } from './passives';
import type { Progression } from './progression';
import { createDiffLog, recordCell, recordRect } from './save';
import type { TerrainDiffLog } from './save';

/** docs/05 §1 — one generator, two settings of the cohesion parameter. */
export type WorldMode = 'blended' | 'layered';

export const COHESION: Readonly<Record<WorldMode, number>> = {
  blended: 0.85,
  layered: 0.2,
};

export type RunOutcomeKind = 'death' | 'extract' | 'abandon';

/** Enemy rank, as the run scores it. Mirrors `entity/enemyData`'s `Rank`. */
export type EnemyRank = 'base' | 'elite' | 'boss';

/**
 * What killed you, in parts — docs/06 §4, docs/23 §3.
 *
 * The parts, not the sentence, because the sentence is said twice: once as the
 * toast the player looks up for, once on the death screen they read afterwards.
 * `causeHeadline` and `causeSentence` below are the only two places either
 * wording exists, so the screen cannot drift from the log the way the seven
 * call sites `Game.announce` replaced had drifted from their toasts.
 *
 * `agentId` deliberately mirrors `RunOutcome.killedBy` rather than replacing it.
 * That field feeds Lineage and is null for the environment on purpose — "you
 * learn no body" — and this record is about what to *say*, which is a different
 * question with a different answer for exactly the case Lineage drops: the lava
 * has no id and is still the most important noun on the screen.
 *
 * Noita composes its own cause line from exactly these two parts. Its session
 * files store `killed_by="Rynkkyhiisi | projectile"` and
 * `killed_by=" | damage from material: lava"` — killer and mechanism, kept
 * separate on disk, joined only for display (docs/23 §3). Noun form, never a
 * verb: it has no "burned to death" string anywhere.
 */
export interface DeathCause {
  /** The display noun: `Rotwood Mother`, `Lava`, `the world`. Never empty. */
  agent: string;
  /** Enemy id when a body did it; null for the environment. */
  agentId: string | null;
  /** Damage type name — `fire`, `acid`, `physical`. `entity/ecs`'s vocabulary. */
  damage: string;
  /** Material label when matter did it, else null. `agent` is already this. */
  material: string | null;
}

/** The toast: `Killed by Lava`. */
export function causeHeadline(c: DeathCause): string {
  return `Killed by ${c.agent}`;
}

/** The log line, which carries the detail the toast does not: `killed by lava (fire)`. */
export function causeSentence(c: DeathCause): string {
  return `killed by ${c.agent.toLowerCase()} (${c.damage})`;
}

export interface RunOutcome {
  kind: RunOutcomeKind;
  /** Enemy id, for Lineage. Null for environmental deaths — you learn no body. */
  killedBy: string | null;
  /**
   * Why it ended, for the death screen to read back — docs/23's spine.
   *
   * Optional because a v4 save predates it and because `extract` and `abandon`
   * have no cause; absent reads as "not recorded", which is the honest value
   * for both. Nothing derives Lineage or any track from it: it is a thing to
   * *show*, and the moment it feeds a number it stops being safe to leave
   * unrecorded on an old save.
   */
  cause?: DeathCause | null;
  atMs: number;
  biome: string;
}

export interface Inventory {
  /** Weapon *instance* ids; combat/ owns the instances themselves. */
  mainHand: string | null;
  offHand: string | null;
  weapons: string[];
  modules: string[];
  /**
   * The run's *view* of the stash — instance ids checked out of it this run.
   *
   * The stash that survives death is `ProgressionState.stash` (issue #5): a run
   * array cannot keep a promise about what happens after the run. This stays
   * because the loadout panel keys "where is it right now" off it.
   */
  stash: string[];
  /**
   * The carried material pouch (issue #8). Material name -> units, capped by
   * `crafting.ts`'s `POUCH_CAP`. Run-scoped and lost with everything else,
   * which is what makes gathering a decision rather than an accumulation.
   */
  materials: Record<string, number>;
  /** Ash picked up this run, unbanked until the run ends. */
  ash: number;
  /**
   * The in-run currency. Earned in the run, spent in the run, **lost on death**
   * — Noita's gold model, where the loss is the point: it forces you to spend
   * rather than hoard.
   *
   * Deliberately not Ash. Ash is meta: it is banked by `bankRun` and buys
   * permanent unlocks. Letting a vendor take Ash would price a weapon you will
   * lose in ten minutes against a permanent unlock, which is a trade no player
   * should be asked to make. Nothing in this file ever moves value between the
   * two.
   */
  scrip: number;
}

/**
 * The vendor ledger — issue #11.
 *
 * Vendors and their stock are pure in `(seed, band, index)` and are therefore
 * *rebuilt*, not saved, exactly like the terrain they stand in (docs/01 §8).
 * What cannot be re-derived is what the player took off the shelf, so that is
 * the only thing recorded. Without it, a save-and-reload restocks every vendor
 * in the world and the currency has no scarcity at all.
 *
 * Run-scoped like everything else here: the ids mean nothing on another seed.
 */
export interface VendorLedger {
  /** Opaque offer ids. Never parse them — `world/vendor.ts` owns their shape. */
  purchased: string[];
}

export interface RunStats {
  kills: number;
  killsBy: Record<string, number>;
  deaths: number;
  damageDealt: number;
  damageTaken: number;
  cellsDestroyed: number;
  /**
   * Cells taken *out* of the world and into the pouch this run — the income
   * side of the material economy (issue #122), and the counterpart to
   * `cellsDestroyed` above.
   *
   * The pair is the point. `cellsDestroyed` counts matter the run turned into
   * rubble; this counts matter the run turned into inventory. Before #122 the
   * second number could only ever have been produced by carrying a scythe
   * (`combat/harvest.ts`), so for most runs it was structurally zero — the
   * economy had sinks, a pouch, ten priced recipes and no income at all.
   */
  cellsGathered: number;
  roomsCleared: number;
  bossesKilled: string[];
  elitesKilled: string[];
}

export interface RoomGraph {
  current: string;
  visited: Set<string>;
  /** "from>to" edge keys — enough to redraw the map without a node table. */
  edges: string[];
}

export interface RunState {
  runId: string;
  seed: number;
  worldMode: WorldMode;
  cohesion: number;
  archetype: string;
  /** The kit this run began with — `meta/kits.ts` owns the table. */
  kit: string;
  /**
   * World modifiers this run was generated with (issue #7).
   *
   * Recorded on the run rather than only read from the profile, because the
   * profile's pick can change while a run is in progress and a saved run must
   * regenerate the world it was actually played in. Same argument as the seed.
   */
  modifiers: string[];

  startedAtMs: number;
  elapsedMs: number;
  /** Sim ticks elapsed. The diff log timestamps against this, not wall clock. */
  tick: number;

  currentBiome: string;
  /** Biomes in arrival order — the run's route, and the Salvage feed. */
  biomeOrder: string[];

  traversal: {
    /** Granted at run start: archetype innates + attuned picks. */
    granted: string[];
    /** Found in-world this run. The Metroidvania half. */
    found: string[];
  };

  inventory: Inventory;
  factions: FactionState;
  /** What the run has already bought from in-world vendors (issue #11). */
  vendors: VendorLedger;
  rooms: RoomGraph;
  stats: RunStats;
  /** Reaction event ids observed this run; deduped against the lifetime codex on bank. */
  discoveries: string[];
  terrain: TerrainDiffLog;
  /**
   * What the run did to its rigid bodies — issue #84.
   *
   * The same argument as `terrain` and as `VendorLedger`, one layer up. *Where*
   * crates are is pure in `(seed, cx, cy)` and is re-derived by
   * `world/bodySites.ts` every time a chunk is generated, so it is not here.
   * What cannot be re-derived is that you smashed one, or pushed one across the
   * room, or burned half of one away — and that is all this holds. An untouched
   * world encodes to an empty array, exactly as an undug one does.
   *
   * Deliberately opaque flat numbers rather than a typed record list.
   * `world/bodySites.ts` owns the layout (`RunBodies.reencode`) the way
   * `meta/save.ts` owns `TerrainDiffLog`'s, and keeping it a plain array is
   * what lets `serializeRun`'s `...rest` spread carry it with no change to
   * `save.ts` and no `SAVE_VERSION` bump. This file imports nothing from
   * `world/` and does not start now.
   *
   * Optional because a v4 save predates it. Absent decodes as an empty ledger,
   * which is the honest reading: that run had no bodies in it at all.
   */
  bodies?: number[];
  outcome: RunOutcome | null;
  /**
   * Issue #172. Charge tracking for whichever `Survivability.revival` kind
   * the run's archetype authors — `kind: 'none'` archetypes carry
   * `chargesRemaining: 0` and it is never read. Lives here rather than on
   * `MoverState` because a `scope: 'run'` charge (Flesh-machine hybrid's
   * restance) must survive exactly what `MoverState` does not: `MoverState`
   * is rebuilt by `createMover` every time the player entity respawns
   * inside a run (a fresh mover for a fresh body), while a *run*-scoped
   * charge is spent once across the whole run. A fresh `RunState` per run
   * (`startNextRun`) already gives `scope: 'run'` its reset for free; `
   * refillRoomRevival` is what `scope: 'room'` needs on top of that.
   */
  revival: {
    chargesRemaining: number;
    /** Room id charges were last refilled against, for `scope: 'room'`. */
    lastRoomId: string | null;
  };
}

export interface CreateRunOptions {
  seed: number;
  worldMode?: WorldMode;
  archetype?: string;
  /** Overrides the profile's stored pick. Falls back to it, then to the default. */
  kit?: string;
  /** Pulls attuned run-start abilities and validates the archetype choice. */
  progression?: Progression;
  /** Overrides the profile's stored opt-in set. Falls back to it (issue #7). */
  modifiers?: readonly string[];
  startBiome?: string;
  startRoom?: string;
  nowMs?: number;
}

function emptyStats(): RunStats {
  return {
    kills: 0,
    killsBy: {},
    deaths: 0,
    damageDealt: 0,
    damageTaken: 0,
    cellsDestroyed: 0,
    cellsGathered: 0,
    roomsCleared: 0,
    bossesKilled: [],
    elitesKilled: [],
  };
}

/** Deterministic from (seed, start time) so bug reports name their own run. */
function makeRunId(seed: number, startedAtMs: number): string {
  return `run-${(seed >>> 0).toString(36)}-${startedAtMs.toString(36)}`;
}

export function createRunState(opts: CreateRunOptions): RunState {
  const nowMs = opts.nowMs ?? Date.now();
  const worldMode = opts.worldMode ?? 'layered';
  const archetypeId = opts.archetype ?? DEFAULT_ARCHETYPE;
  const archetype = getArchetype(archetypeId);

  if (opts.progression && !opts.progression.unlockedArchetypes().includes(archetypeId)) {
    throw new Error(`archetype not unlocked: ${archetypeId}`);
  }

  // Attunement is the only path from earned abilities to run-start abilities.
  // With zero slots this list is empty and the run is a pure Metroidvania.
  const attuned = opts.progression ? opts.progression.effectiveStartAbilities() : [];
  const granted = [...new Set<string>([...archetype.innateAbilities, ...attuned])];

  // The kit is resolved here rather than trusted, so a run created from a URL
  // parameter or a stale profile still starts with something in its hands.
  const kit = getKit(opts.kit ?? opts.progression?.effectiveStartKit()).id;

  // Opt-in, and validated against what the profile owns: `effectiveWorldModifiers`
  // drops anything no longer unlocked, so a stale pick costs the modifier and
  // not the run (issue #7).
  const modifiers = opts.modifiers
    ? [...new Set(opts.modifiers)]
    : (opts.progression?.effectiveWorldModifiers() ?? []);

  // Ash Courier (issue #4). Consumed here — read-and-clear in one call — so the
  // carry can never be collected by two runs. Without the passive this is 0 and
  // a run still starts with an empty pocket, which is the existing contract.
  const passives: RunPassives = opts.progression ? resolvePassives(opts.progression) : NO_PASSIVES;
  const carriedAsh = opts.progression ? opts.progression.consumeCarriedAsh() : 0;

  const startRoom = opts.startRoom ?? 'room:origin';

  // Cartographer's Echo (issue #4). Only for the seed it was drawn on: room ids
  // replayed against a different world would draw a map of rooms that are not
  // there, which is worse than no map because the player would trust it.
  const echo = passives.mapEcho
    ? echoForSeed(opts.progression?.mapEcho(), opts.seed >>> 0)
    : null;
  const visited = new Set<string>([startRoom, ...(echo?.visited ?? [])]);
  const edges = [...(echo?.edges ?? [])];

  return {
    runId: makeRunId(opts.seed, nowMs),
    seed: opts.seed >>> 0,
    worldMode,
    cohesion: COHESION[worldMode],
    archetype: archetypeId,
    kit,
    modifiers,

    startedAtMs: nowMs,
    elapsedMs: 0,
    tick: 0,

    // No placeholder biome. This used to default to `the-sunken-vault`, an id
    // `biomes.ts` spells `sunken_vault` — so every run banked Salvage for a
    // biome that does not exist, and then deduped the real one against it.
    // The run learns where it is from `enterBiome` on the first sim tick.
    currentBiome: opts.startBiome ?? '',
    biomeOrder: opts.startBiome ? [opts.startBiome] : [],

    traversal: { granted, found: [] },

    inventory: {
      mainHand: null, offHand: null, weapons: [], modules: [], stash: [],
      materials: {},
      // Ash may arrive carried (Ash Courier); scrip is unconditionally zero,
      // because there is no parameter that could carry a balance in from a
      // previous run. "Lost on death" is therefore a property of run creation
      // rather than a rule someone must remember to enforce at the death site —
      // and the asymmetry between the two lines is exactly the difference
      // between a meta currency and a run one.
      ash: carriedAsh, scrip: 0,
    },
    factions: createFactionState(),
    vendors: { purchased: [] },
    rooms: { current: startRoom, visited, edges },
    stats: emptyStats(),
    discoveries: [],
    terrain: createDiffLog(opts.seed >>> 0),
    bodies: [],
    outcome: null,
    revival: { chargesRemaining: archetype.survivability.revival.charges, lastRoomId: startRoom },
  };
}

// --------------------------------------------------------------- resuming

/**
 * **What "Continue" means, decided explicitly — issue #51.**
 *
 * The run was serialised on every autosave and nothing ever read it back, so
 * before this there was no answer to the question at all. Here is the answer,
 * and it is a *decision*, not a description of whatever happened to be in the
 * envelope:
 *
 * > **Continue resumes the run. The world it was played in is rebuilt from the
 * > run's seed, and the player re-enters it at the entrance, at full health,
 * > holding the kit the run began with.**
 *
 * Field by field, because "the run state" is not specific enough to be honest:
 *
 * | Field | Restored? | Why |
 * |---|---|---|
 * | `runId`, `seed`, `worldMode`, `cohesion` | yes | It is the same run in the same world, or it is a new run wearing an old name. The seed is the whole reason a resume is possible without serialising cells (docs/01 §8). |
 * | `archetype`, `kit`, `modifiers` | yes | Chosen once at setup; re-choosing them mid-run would change what the run *is*. `modifiers` is stored on the run rather than read from the profile for exactly this case. |
 * | `startedAtMs`, `elapsedMs`, `tick` | yes | The run timer is a speedrun surface. A resume that reset it would make the clock a lie; one that kept counting through the shutdown would make it a different lie. |
 * | `currentBiome`, `biomeOrder` | yes | Depth is the run's progress. This is the field a player means by "I was three biomes down". |
 * | `traversal.granted`, `traversal.found` | yes | A found ability that did not come back would re-lock a door the player has already opened — the one loss that would make the Metroidvania half unplayable. |
 * | `inventory.ash`, `.scrip`, `.materials` | yes | Earned in the run and lost with it. Losing them to a *quit* rather than to a death would make the shutdown a punishment the design never chose. |
 * | `factions`, `vendors.purchased` | yes | Standing is earned play, and the ledger is the only thing stopping a reload from restocking every vendor in the world (see `VendorLedger`). |
 * | `rooms`, `stats`, `discoveries` | yes | The map you drew and the tally you built. |
 * | `terrain` | yes, as a diff | Replayed over the regenerated world (`applyDiffLog`). `world/runTerrain.ts` is both halves — it records every cell `CellWorld.damageCell` destroys and lays them back down as chunks generate (#117). It used to be neither, so the log was empty for every run ever played and a resume silently discarded every hole the player had dug. |
 * | `bodies` | yes, as a diff | Crates are **re-derived** at the poses the seed authored, and this replays what the run did to them (#84). A crate you broke is still broken; a crate you pushed is where you pushed it, with the damage you did to it. A crate that was *on fire* is not on fire — that is cell state, and it goes the way the cave's fires go. |
 * | `outcome` | must be `null` | A run with an outcome has been banked. Resuming it would pay its Ash a second time and hand the player a corpse. That is the predicate below. |
 * | `inventory.mainHand`, `.offHand`, `.weapons`, `.modules`, `.stash` | ids survive, **the weapons do not** | These are instance ids; `combat/` owns the instances and nothing serialises them. Nothing writes these fields today either, so they are empty in practice — but the rule stands: **a resumed run does not restore the weapons in your hands.** The kit is re-issued instead. |
 *
 * And what is **not** in `RunState` at all, so cannot come back and is not
 * claimed to:
 *
 * - **The cell world's state.** The cave is the *same cave* — generation is pure
 *   in the seed — but everything the simulation did inside it is gone: fires,
 *   spilled liquids, fallen sand, craters. Serialising cells is the one thing
 *   docs/01 §8 rules out, and this is the price of that rule.
 * - **Where the player was standing, their health, their statuses.** The run
 *   re-enters at the spawn point at full health. A saved position pointing into
 *   a world that has re-settled around it is worse than an honest restart.
 * - **Enemies, altar timers, ability caches, vendors, rest nodes.** All pure in
 *   `(seed, band, index)` and rebuilt, which is why only `vendors.purchased`
 *   needed saving. **Crate placements** joined that list with #84 — pure in
 *   `(seed, cx, cy)` and rebuilt, which is why only `bodies` needed saving.
 *
 * The title screen says so on the button (`buildTitleVM`), because a player who
 * continues and finds the cave rearranged should have been told beforehand.
 *
 * Note what resuming deliberately *skips*: `createRunState` consumes the Ash
 * Courier's carry and stamps the Cartographer's Echo. Resuming does not call it,
 * so neither is collected twice — a property of not creating a run rather than a
 * rule someone has to remember.
 */
export function isResumable(run: RunState | null | undefined): run is RunState {
  return !!run && run.outcome === null;
}

// ------------------------------------------------------------------ clock

/**
 * Advance the run's two clocks — issue #118.
 *
 * Called once per **fixed simulation step**, from `core/game.ts`'s
 * `stepSimulation`, beside `cells.tick++`. Three properties follow from that
 * call site and none of them is incidental:
 *
 * - **Simulated time, not wall time.** `dtMs` is the fixed step, so the clock
 *   counts steps the simulation actually took. A frame that dropped four sim
 *   steps to the catch-up clamp did not simulate that second and does not
 *   charge the player for it, and two machines that ran the same inputs read
 *   the same time. `startedAtMs` is the wall clock and stays a wall clock;
 *   these two are the run's own.
 * - **Paused is stopped.** The fixed-step loops are inside `if (!this.paused)`,
 *   so a run parked on the pause menu does not tick. That is the only reading
 *   of "elapsed" a speedrun surface can defend.
 * - **`tick` is the sim tick, and terrain diffs are keyed on it.** `RunTerrain`
 *   merges cells destroyed *on the same tick* into one span, so a frozen tick
 *   made "same frame" and "a minute apart" the same key. One increment per
 *   `stepSimulation` is what makes that key mean what it says.
 *
 * Both fields survive a save (`isResumable`'s table), and this only ever adds,
 * so a resumed run continues its own clock instead of restarting it or counting
 * the hours the game was shut.
 */
export function advanceRun(run: RunState, dtMs: number, ticks = 0): void {
  run.elapsedMs += dtMs;
  run.tick += ticks;
}

/**
 * mm:ss.cs for the HUD — run timers are a speedrun surface, so show hundredths.
 *
 * Floors, with a one-microsecond snap, and the snap is not paranoia — it is the
 * defect #118 exposed the moment the clock started moving. `elapsedMs` is a
 * float accumulation of 1/60 s steps, and 1/60 s is not representable. Three
 * steps are exactly 50 ms in the reals, so at 60 Hz **every third frame lands
 * on a hundredth boundary** — and the accumulated sum lands a few ulps *below*
 * it. Measured over an hour of stepping (216 000 steps): the sum drifts by at
 * most 1.01e-5 ms, but 38 436 frames — 17.8% of them — floored to a hundredth
 * that had already passed, printing 0:00.99 for a second that was fully
 * elapsed. A timer that shows the wrong hundredth on a sixth of its frames is
 * not a rounding preference. The snap is four orders of magnitude smaller than
 * the worst drift it corrects and eight below the unit displayed, so it cannot
 * round a time up past a boundary anything could observe.
 */
export function formatRunTime(ms: number): string {
  const total = Math.max(0, Math.floor(ms + 1e-3));
  const m = Math.floor(total / 60000);
  const s = Math.floor((total % 60000) / 1000);
  const cs = Math.floor((total % 1000) / 10);
  return `${m}:${String(s).padStart(2, '0')}.${String(cs).padStart(2, '0')}`;
}

// -------------------------------------------------------------- traversal

export function hasAbility(run: RunState, id: string): boolean {
  return run.traversal.granted.includes(id) || run.traversal.found.includes(id);
}

/** Returns false if already held — the caller can then skip the pickup fanfare. */
export function grantAbility(run: RunState, id: TraversalAbilityId | string): boolean {
  if (hasAbility(run, id)) return false;
  run.traversal.found.push(id);
  return true;
}

export function abilities(run: RunState): string[] {
  return [...new Set([...run.traversal.granted, ...run.traversal.found])];
}

// ------------------------------------------------------------------ world

export function enterRoom(run: RunState, roomId: string): boolean {
  const from = run.rooms.current;
  if (from && from !== roomId) {
    const edge = `${from}>${roomId}`;
    if (!run.rooms.edges.includes(edge)) run.rooms.edges.push(edge);
  }
  run.rooms.current = roomId;
  if (run.rooms.visited.has(roomId)) return false;
  run.rooms.visited.add(roomId);
  return true;
}

/**
 * Issue #172. Refills `scope: 'room'` revival charges once the room has
 * actually changed since the last refill — `enterRoom`'s own return value
 * answers "visited for the first time ever," not "changed," so this
 * compares `run.rooms.current` against the last room refilled against
 * instead, after `enterRoom` has already updated it for this call.
 * `scope: 'run'` and `scope: 'never'` are no-ops here: a `'run'` charge is
 * spent once across the whole run (a fresh `RunState` per run already
 * resets it), and a `'never'` archetype's `charges` is 0 to begin with.
 */
export function refillRoomRevival(run: RunState, revival: Revival): void {
  if (revival.scope !== 'room') return;
  if (run.revival.lastRoomId === run.rooms.current) return;
  run.revival.lastRoomId = run.rooms.current;
  run.revival.chargesRemaining = revival.charges;
}

export function enterBiome(run: RunState, biomeId: string): boolean {
  run.currentBiome = biomeId;
  if (run.biomeOrder.includes(biomeId)) return false;
  run.biomeOrder.push(biomeId);
  return true;
}

// -------------------------------------------------------------- inventory

export function addWeapon(run: RunState, instanceId: string): void {
  if (!run.inventory.weapons.includes(instanceId)) run.inventory.weapons.push(instanceId);
}

export function addModule(run: RunState, moduleId: string): void {
  run.inventory.modules.push(moduleId);
}

export function equip(run: RunState, hand: 'main' | 'off', instanceId: string | null): void {
  if (hand === 'main') run.inventory.mainHand = instanceId;
  else run.inventory.offHand = instanceId;
}

export function addAsh(run: RunState, amount: number): void {
  run.inventory.ash += Math.max(0, amount);
}

export function addScrip(run: RunState, amount: number): void {
  run.inventory.scrip += Math.max(0, amount);
}

/**
 * Spend. Returns false and moves nothing when the balance is short, so a caller
 * can offer the transaction and let this refuse it — a partial debit is worse
 * than a refused one.
 */
export function spendScrip(run: RunState, amount: number): boolean {
  const cost = Math.max(0, Math.floor(amount));
  if (run.inventory.scrip < cost) return false;
  run.inventory.scrip -= cost;
  return true;
}

/**
 * Kill payouts by rank. Scrip runs about twice Ash because it is spent inside
 * the run it is earned in and vanishes at death, where Ash compounds forever.
 */
export const ASH_BY_RANK: Readonly<Record<EnemyRank, number>> = { base: 3, elite: 12, boss: 40 };
export const SCRIP_BY_RANK: Readonly<Record<EnemyRank, number>> = { base: 6, elite: 25, boss: 90 };

// ------------------------------------------------------------------ stats

/**
 * A kill, and everything a kill pays.
 *
 * The payout lives *inside* the kill rather than beside it at the call site,
 * because beside it is where `addAsh` sat unwired for the entire project: the
 * balance was permanently zero and all twelve Ash unlocks were unbuyable. A
 * reward you have to remember to call is a reward that eventually is not
 * called. Recording a kill and paying for it are now the same operation, and
 * there is exactly one place to read the rates.
 */
export interface KillReward {
  ash: number;
  scrip: number;
  /**
   * Which factions moved, and by how much — both the one you robbed of a
   * member and the one that profited. Empty for an untagged enemy. The caller
   * uses this to *tell the player*, which is the acceptance criterion issue #11
   * puts before any shop exists.
   */
  reputation: RepChange[];
}

export function recordKill(
  run: RunState,
  enemyId: string,
  tier: EnemyRank = 'base',
  factions: readonly FactionId[] = [],
): KillReward {
  run.stats.kills += 1;
  run.stats.killsBy[enemyId] = (run.stats.killsBy[enemyId] ?? 0) + 1;
  if (tier === 'boss' && !run.stats.bossesKilled.includes(enemyId)) run.stats.bossesKilled.push(enemyId);
  if (tier === 'elite' && !run.stats.elitesKilled.includes(enemyId)) run.stats.elitesKilled.push(enemyId);

  const ash = ASH_BY_RANK[tier];
  const scrip = SCRIP_BY_RANK[tier];
  addAsh(run, ash);
  addScrip(run, scrip);
  // Reputation rides inside the kill for exactly the reason the payout does.
  // `applyDeed` and `runFactions` had zero call sites for the whole project
  // (docs/13 §3), and the fix that lasts is not "remember to call it at the
  // death site" — it is having no separate site to forget.
  const reputation = runFactions(run).slay(factions, tier);
  return { ash, scrip, reputation };
}

/**
 * The narrowest view of a run that the gather verb needs — issue #122.
 *
 * Structural, and `RunState` satisfies it, for the same reason `MaterialPouch`
 * in `meta/crafting.ts` is structural: the verb lives in `combat/`, and a combat
 * module that had to name `RunState` to tick a counter would put the save file's
 * lifetime on a combat type. `combat/gather.ts` therefore names this and the
 * pouch, and never the run.
 */
export interface MaterialLedger {
  stats: { cellsGathered: number };
}

/**
 * Book `units` of matter as income. Returns the run's new total, which is the
 * number the gather verb reads back for its own event line — the one reader
 * `cellsGathered` has today, and the reason it is not another authored field
 * nothing consumes.
 *
 * `?? 0` because `save.ts` spreads `stats` through `deserializeRun` untouched,
 * so a run saved before this field existed comes back without it, and `+=` on
 * `undefined` is `NaN` — a corrupted counter rather than a missing one.
 */
export function recordGathered(run: MaterialLedger, units: number): number {
  const n = Math.max(0, Math.floor(units));
  run.stats.cellsGathered = (run.stats.cellsGathered ?? 0) + n;
  return run.stats.cellsGathered;
}

/** Reaction observed. Deduped per-run; deduped again lifetime-wide on bank. */
export function recordDiscovery(run: RunState, eventId: string): boolean {
  if (run.discoveries.includes(eventId)) return false;
  run.discoveries.push(eventId);
  return true;
}

// --------------------------------------------------------------- factions

export function runFactions(run: RunState): Factions {
  return new Factions(run.factions);
}

export function applyDeed(run: RunState, deed: DeedId, times = 1): number {
  return runFactions(run).applyDeed(deed, times);
}

// ---------------------------------------------------------------- vendors

export function vendorSold(run: RunState, offerId: string): boolean {
  return run.vendors.purchased.includes(offerId);
}

/**
 * Record a purchase. Separate from `spendScrip` on purpose: the debit is what
 * the transaction *costs* and this is what it *consumes*, and a vendor that
 * takes payment must be able to fail the second half loudly rather than
 * silently sell the same module twice.
 */
export function markVendorPurchase(run: RunState, offerId: string): void {
  if (!run.vendors.purchased.includes(offerId)) run.vendors.purchased.push(offerId);
}

// ---------------------------------------------------------------- terrain

/**
 * A cell was destroyed — issue #117.
 *
 * `mat` is what is there *after*, not what was there before: `damageCell`
 * either voids the cell or shatters a brittle material into its rubble, and
 * the log records the world's new state either way.
 *
 * The producer is `world/runTerrain.ts`, hooked to `CellWorld.onCellDamaged`,
 * which is the single gate every weapon, ability, explosion, corrosive splash
 * and burrowing enemy already passes through. **Only destruction is recorded.**
 * Everything the simulation does on its own — falling sand, flowing liquid,
 * fire, thermal melting, reaction products, support collapse — reaches cells
 * through `set`/`swap`/`transmute` and is deliberately absent, which is both
 * the promise the resume table above makes and the thing that keeps this log
 * bounded: a fire that burns a biome writes nothing here.
 */
export function noteCellChange(run: RunState, x: number, y: number, mat: number): void {
  recordCell(run.terrain, run.tick, x, y, mat);
}

/**
 * A contiguous run of destroyed cells, as one entry.
 *
 * The coalescing rule, stated once and enforced in `world/runTerrain.ts`:
 * **cells destroyed on the same tick, on the same row, with the same resulting
 * material, in a contiguous span, are one entry.** Every bulk destroyer in the
 * game is a `damageCircle`, which scans row by row, so this is worth about `r`
 * to `1` on an explosion and grows with the weapon.
 */
export function noteRectChange(
  run: RunState,
  x: number,
  y: number,
  w: number,
  h: number,
  mat: number,
): void {
  recordRect(run.terrain, run.tick, x, y, w, h, mat);
}

// ------------------------------------------------------------------ ending

export function endRun(
  run: RunState,
  kind: RunOutcomeKind,
  killedBy: string | null = null,
  nowMs?: number,
  cause: DeathCause | null = null,
): RunOutcome {
  if (kind === 'death') run.stats.deaths += 1;
  run.outcome = {
    kind,
    killedBy,
    cause,
    atMs: nowMs ?? run.startedAtMs + run.elapsedMs,
    biome: run.currentBiome,
  };
  return run.outcome;
}

export interface BankReport {
  ash: number;
  /**
   * Unbanked ash the Ash Courier passive carried into the next run. Zero
   * without the passive, and reported separately from `ash` because they are
   * different promises: `ash` went into the permanent balance, this went into
   * the next run's pocket.
   */
  ashCarried: number;
  /** True when this run's room graph was kept for the next run on this seed. */
  mapEchoed: boolean;
  /**
   * Scrip the run was carrying when it ended. Nothing is done with it — it is
   * reported so the integrator can *tell the player* what the death cost, which
   * is the difference between a currency with stakes and one that looks broken.
   */
  scripLost: number;
  codexNew: number;
  biomesNew: number;
  bossesNew: number;
  elitesNew: number;
  lineage: { archetype: string | null; amount: number };
  unlockCount: number;
}

/**
 * The single seam between run-scoped and persistent state. Call once, after
 * `endRun`. Every track is fed here and nowhere else, which is what makes
 * "which behaviour feeds which track" auditable in one function.
 */
export function bankRun(run: RunState, prog: Progression): BankReport {
  const before = prog.peekPending().length;

  for (const e of run.discoveries) prog.recordDiscovery(e);
  for (const b of run.biomeOrder) prog.recordBiomeReached(b);
  for (const b of run.stats.bossesKilled) prog.recordBossKill(b);
  for (const e of run.stats.elitesKilled) prog.recordEliteKill(e);

  prog.state.lifetime.kills += run.stats.kills;
  prog.state.lifetime.playtimeMs += run.elapsedMs;

  const outcome = run.outcome ?? endRun(run, 'abandon');
  let lineage: BankReport['lineage'] = { archetype: null, amount: 0 };
  if (outcome.kind === 'death') {
    const r = prog.recordDeath(outcome.killedBy);
    lineage = { archetype: r.archetype, amount: r.amount };
  }

  const ashBefore = prog.earned('ash');
  prog.recordRunEnd(run.inventory.ash, outcome.kind);
  const ash = prog.earned('ash') - ashBefore;

  // Passives are read here rather than passed in, so the two things that fire on
  // a run ending cannot disagree about what the profile holds (issue #4).
  const passives = resolvePassives(prog);

  // Ash Courier: 20% of what was *not* banked rides into the next run. Only on
  // a death — the unlock says "through death", and an extraction has already
  // been paid at the full rate, so paying it again here would make the courier
  // a flat bonus rather than a consolation.
  let ashCarried = 0;
  if (outcome.kind === 'death' && passives.ashCarry > 0) {
    ashCarried = Math.floor(run.inventory.ash * passives.ashCarry);
    if (ashCarried > 0) prog.setCarriedAsh(prog.carriedAsh() + ashCarried);
  }

  // Cartographer's Echo: keep the route, keyed on the seed that drew it. Written
  // on a death because that is the run the unlock's copy names — an extraction
  // is not a "failed run", and echoing a successful route into the next one
  // would erase the reason to explore it.
  let mapEchoed = false;
  if (outcome.kind === 'death' && passives.mapEcho && run.rooms.visited.size > 0) {
    prog.setMapEcho(makeMapEcho(run.seed, run.rooms.visited, run.rooms.edges));
    mapEchoed = true;
  }

  // Scrip is deliberately *not* fed to any track, on any outcome. It is the one
  // value in the run that meta progression never sees; the run object is
  // discarded after this and the currency dies with it. The material pouch dies
  // with it too, for the same reason and by the same silence (issue #8).
  const scripLost = run.inventory.scrip;

  return {
    ash,
    ashCarried,
    mapEchoed,
    scripLost,
    codexNew: run.discoveries.length,
    biomesNew: run.biomeOrder.length,
    bossesNew: run.stats.bossesKilled.length,
    elitesNew: run.stats.elitesKilled.length,
    lineage,
    unlockCount: prog.peekPending().length - before,
  };
}

export interface RunSummary {
  runId: string;
  seed: number;
  worldMode: WorldMode;
  archetype: string;
  kit: string;
  time: string;
  biomes: number;
  rooms: number;
  abilities: number;
  kills: number;
  ash: number;
  scrip: number;
  outcome: RunOutcomeKind | 'in-progress';
}

export function runSummary(run: RunState): RunSummary {
  return {
    runId: run.runId,
    seed: run.seed,
    worldMode: run.worldMode,
    archetype: run.archetype,
    kit: run.kit,
    time: formatRunTime(run.elapsedMs),
    biomes: run.biomeOrder.length,
    rooms: run.rooms.visited.size,
    abilities: abilities(run).length,
    kills: run.stats.kills,
    ash: run.inventory.ash,
    scrip: run.inventory.scrip,
    outcome: run.outcome?.kind ?? 'in-progress',
  };
}
